<template>
  <div class='space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
    <div class='space-y-3'>
      <VerificationMethodResult
        v-for='methodResult in visibleMethodResults'
        :key='`${actionResult.action}-${methodResult.methodType}-${methodResult.methodName}`'
        :method-result='methodResult'
        :verification-uuid='verificationUuid'
        :action-type='actionResult.action'
        :method-type='methodResult.methodType'
      />
      <button
        v-if='hasFallbackMethods'
        type='button'
        class='text-sm font-semibold text-gray-600 transition hover:text-gray-900'
        @click='fallbackExpanded = !fallbackExpanded'
      >
        {{ fallbackExpanded ? `Hide ${fallbackLabel}` : `Proceed with ${fallbackLabel}` }}
      </button>
      <div
        v-if='!actionResult.methodResults.length'
        class='rounded border border-dashed border-gray-300 p-3 text-sm text-gray-500'
      >
        No method results for this action.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import VerificationMethodResult from '@/components/actions/CustomActions/Global/Verification/VerificationMethodResult.vue'
  import type { VerificationActionMethodResult, VerificationActionResult } from '@/api/requests/verification/verificationResponses'
  import { isVerificationMethodStatusSuccessful } from '@/utils/verification'

  const props = defineProps<{ actionResult: VerificationActionResult; verificationUuid: string | null }>()

  const fallbackExpanded = ref(false)

  const isPasskey = (method: VerificationActionMethodResult) => method.methodType.toLowerCase() === 'passkey'
  const isSuccessful = (method: VerificationActionMethodResult) => isVerificationMethodStatusSuccessful(method.challengeStatus)

  const primaryMethod = computed(() => {
    const successful = props.actionResult.methodResults.find(isSuccessful)

    if (successful) {
      return successful
    }

    return props.actionResult.methodResults.find(isPasskey)
      ?? props.actionResult.methodResults[0]
      ?? null
  })

  const fallbackMethods = computed(() => {
    if (!primaryMethod.value) {
      return []
    }

    return props.actionResult.methodResults.filter(method => method !== primaryMethod.value)
  })

  const hasFallbackMethods = computed(() => fallbackMethods.value.length > 0)
  const fallbackLabel = computed(() => {
    const firstFallback = fallbackMethods.value[0]

    if (!firstFallback) {
      return 'another method'
    }

    return firstFallback.methodType.toLowerCase() === 'totp'
      ? 'TOTP code'
      : firstFallback.methodType
  })

  const visibleMethodResults = computed(() => {
    if (!primaryMethod.value) {
      return []
    }

    return fallbackExpanded.value
      ? [primaryMethod.value, ...fallbackMethods.value]
      : [primaryMethod.value]
  })

  watch(() => props.actionResult.action, () => {
    fallbackExpanded.value = false
  })
</script>
